<script setup lang="ts">
import { ref, watch, computed, onBeforeUnmount, nextTick } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useApi, UiCard, UiProgressBar, UiCheckbox, UiButton, UiText, UiInputField, UiDialog, UiIcon, UiAvatar } from '@shopexperts/shared';
import { expertBulkActions, type ExpertBulkAction } from '@/app/composables/expert-manager/expertBulkActions';
import { resolveShiftRange } from '@/app/services/partnerDirectoryRangeSelect.util';
import { useExpertAiBatches } from '@/app/composables/expert-manager/useExpertAiBatches';
import { AI_SUMMARY_STEP_LABELS } from '@/app/composables/expert-manager/aiSummaries';
import { useAdminExperts } from '@/app/composables/expert-manager/useAdminExperts';
import { useExpertFilterOptions } from '@/app/composables/expert-manager/useExpertFilterOptions';
import { useExpertStatusActions } from '@/app/composables/expert-manager/useExpertStatusActions';
import { useLoginAsExpert } from '@/app/composables/expert-manager/useLoginAsExpert';
import { useExpertTagCatalog } from '@/app/composables/expert-manager/useExpertTags';
import { useExpertFilterPresets } from '@/app/composables/expert-manager/useExpertFilterPresets';
import {
  createEmptyExpertFilters,
  describeActiveExpertFilters,
  expertFiltersEqual,
  expertFiltersFromQuery,
  expertFiltersFromSnapshot,
  expertFiltersToQuery,
  expertFiltersToSnapshot,
  hasAnyExpertFilter,
  removeExpertFilter,
  type ActiveExpertFilter,
  type ExpertManagerFilterState,
} from '@/app/composables/expert-manager/expertManagerFilters';
import { getExpertTypeAppearance } from '@/app/components/expert-manager/expertTypeAppearance';
import ExpertCard from '@/app/components/expert-manager/ExpertCard.vue';
import ReassignExpertLevelDialog from '@/app/components/expert-manager/wallet/ReassignExpertLevelDialog.vue';
import ExpertFilters from '@/app/components/expert-manager/ExpertFilters.vue';
import ExpertPresetTabs from '@/app/components/expert-manager/ExpertPresetTabs.vue';
import ExpertPresetDialog from '@/app/components/expert-manager/ExpertPresetDialog.vue';
import ExpertStatsPanel from '@/app/components/expert-manager/ExpertStatsPanel.vue';
import StatusConfirmDialog from '@/app/components/expert-manager/StatusConfirmDialog.vue';
import ExpertTypeSwitchDialog from '@/app/components/expert-manager/ExpertTypeSwitchDialog.vue';
import ExpertInviteEmailDialog from '@/app/components/expert-manager/ExpertInviteEmailDialog.vue';
import EmptyDataPlaceholder from '@/components/common/EmptyDataPlaceholder.vue';
import LoadingCard from '@/components/common/LoadingCard.vue';
import type { AdminExpertListItem, ExpertFilterPreset, ExpertProfileStatus, ListingType } from '@shopexperts/legacy-api';

const api = useApi();
const { jobs: aiJobs, track: trackAiBatch, dismiss: dismissAiBatch, storageError: aiTrackingError, discoveryError: aiDiscoveryError } = useExpertAiBatches();
const route = useRoute();
const router = useRouter();
const SEARCH_INPUT_ID = 'admin-expert-manager-search';
const ALL_TAB = 'all';

const SCROLL_STORAGE_KEY = 'admin-expert-manager:scrollTop';

// Hydrate state from URL query so navigating back from the detail page
// (via the in-app back button OR the browser back button) restores the
// previous search / filters / tab.
const filters = ref<ExpertManagerFilterState>(expertFiltersFromQuery(route.query));
const activeTabId = ref<string>(typeof route.query.preset === 'string' ? route.query.preset : ALL_TAB);

// A link carrying only `?preset=3` has no filters to hydrate from, so the
// preset's own snapshot is applied once the preset list arrives.
const needsPresetHydration = ref(
  activeTabId.value !== ALL_TAB && !hasAnyExpertFilter(filters.value),
);

/**
 * In-progress filters per tab, so switching tabs never silently drops work:
 * come back to a tab and you get your edit, still flagged as unsaved, rather
 * than the stored snapshot. Cleared for a tab once it is saved or discarded.
 */
const pendingFilters = new Map<string, ExpertManagerFilterState>([
  [activeTabId.value, { ...filters.value }],
]);

const searchQuery = computed({
  get: () => filters.value.search,
  set: (value: string) => { filters.value = { ...filters.value, search: value }; },
});

const scrollContainer = ref<HTMLElement | null>(null);

// Persist filters/search/tab in the URL using router.replace so browser history
// stays clean (no new entry per keystroke). Debounced so rapid typing in the
// search box doesn't thrash the router.
let syncTimer: ReturnType<typeof setTimeout> | null = null;

async function syncQueryToUrl() {
  const next: Record<string, string> = expertFiltersToQuery(filters.value);
  if (activeTabId.value !== ALL_TAB) next.preset = activeTabId.value;

  // Avoid no-op replaces (which can still trigger router internals).
  const current = route.query;
  const sameKeys =
    Object.keys(next).length === Object.keys(current).length &&
    Object.entries(next).every(([k, v]) => current[k] === v);
  if (sameKeys) return;

  await router.replace({ query: next });
}

watch([filters, activeTabId], () => {
  if (syncTimer) clearTimeout(syncTimer);
  syncTimer = setTimeout(syncQueryToUrl, 200);
}, { deep: true });

onBeforeUnmount(() => {
  if (syncTimer) clearTimeout(syncTimer);
});

const { filterOptions } = useExpertFilterOptions();
const { tagOptions, tagNameById, isLoading: isTagsLoading } = useExpertTagCatalog();
const {
  presets,
  isLoading: isPresetsLoading,
  createPreset,
  updatePreset,
  deletePreset,
} = useExpertFilterPresets();

const activePreset = computed<ExpertFilterPreset | null>(() => {
  if (activeTabId.value === ALL_TAB) return null;
  return presets.value.find((preset) => String(preset.id) === activeTabId.value) ?? null;
});

const isPresetDirty = computed(() => {
  if (!activePreset.value) return false;
  return !expertFiltersEqual(filters.value, expertFiltersFromSnapshot(activePreset.value.filters));
});

watch(presets, (list) => {
  if (activeTabId.value === ALL_TAB) return;
  const preset = list.find((item) => String(item.id) === activeTabId.value);
  if (!preset) {
    // The tab was deleted (possibly in another session) — fall back to All.
    if (list.length || !isPresetsLoading.value) activeTabId.value = ALL_TAB;
    return;
  }
  if (needsPresetHydration.value) {
    needsPresetHydration.value = false;
    filters.value = expertFiltersFromSnapshot(preset.filters);
  }
});

function selectTab(tabId: string) {
  if (tabId === activeTabId.value) return;
  pendingFilters.set(activeTabId.value, { ...filters.value });

  activeTabId.value = tabId;
  needsPresetHydration.value = false;

  const pending = pendingFilters.get(tabId);
  if (pending) {
    filters.value = { ...pending };
    return;
  }
  if (tabId === ALL_TAB) {
    filters.value = createEmptyExpertFilters();
    return;
  }
  const preset = presets.value.find((item) => String(item.id) === tabId);
  filters.value = expertFiltersFromSnapshot(preset?.filters);
}

const activeFilters = computed<ActiveExpertFilter[]>(() =>
  describeActiveExpertFilters(filters.value, tagNameById.value),
);

function clearFilter(key: ActiveExpertFilter['key']) {
  filters.value = removeExpertFilter(filters.value, key);
}

function resetFilters() {
  filters.value = { ...createEmptyExpertFilters(), search: filters.value.search };
}

function discardPresetChanges() {
  if (!activePreset.value) return;
  pendingFilters.delete(activeTabId.value);
  filters.value = expertFiltersFromSnapshot(activePreset.value.filters);
}

// Preset dialogs
const presetDialogOpen = ref(false);
const presetDialogMode = ref<'create' | 'rename'>('create');
const presetDialogTarget = ref<ExpertFilterPreset | null>(null);
const isPresetMutating = computed(() => createPreset.isPending.value || updatePreset.isPending.value);
const presetNames = computed(() => presets.value.map((preset) => preset.name));

function openCreatePreset() {
  presetDialogMode.value = 'create';
  presetDialogTarget.value = null;
  presetDialogOpen.value = true;
}

function openRenamePreset(preset: ExpertFilterPreset) {
  presetDialogMode.value = 'rename';
  presetDialogTarget.value = preset;
  presetDialogOpen.value = true;
}

// Every mutation already toasts through its own onError; these handlers swallow
// the rejection so an API failure doesn't surface as an unhandled rejection —
// and so the dialog stays open on the values the admin can still fix.
async function submitPresetDialog(name: string) {
  if (presetDialogMode.value === 'rename') {
    const target = presetDialogTarget.value;
    if (!target) return;
    try {
      // Rename keeps the stored snapshot; the API replaces the whole record.
      await updatePreset.mutateAsync({ presetId: target.id, name, filters: target.filters });
      presetDialogOpen.value = false;
    } catch {
      // Keep the dialog open; the toast carries the reason.
    }
    return;
  }

  try {
    const created = await createPreset.mutateAsync({
      name,
      filters: expertFiltersToSnapshot(filters.value),
    });
    presetDialogOpen.value = false;
    // The filters now live in the new tab; the originating tab keeps whatever
    // it had, and the new tab starts clean against its own snapshot.
    pendingFilters.delete(activeTabId.value);
    activeTabId.value = String(created.id);
  } catch {
    // Keep the dialog open so the name can be corrected (e.g. duplicate name).
  }
}

async function savePresetFilters(preset: ExpertFilterPreset) {
  try {
    await updatePreset.mutateAsync({
      presetId: preset.id,
      name: preset.name,
      filters: expertFiltersToSnapshot(filters.value),
    });
    // Saved: the snapshot now matches, so there is no pending edit to keep.
    pendingFilters.delete(String(preset.id));
  } catch {
    // Tab stays dirty, which is the correct state after a failed save.
  }
}

// Delete preset confirmation
const deletePresetTarget = ref<ExpertFilterPreset | null>(null);
const deletePresetOpen = ref(false);
const isDeletingPreset = computed(() => deletePreset.isPending.value);

function confirmDeletePreset(preset: ExpertFilterPreset) {
  deletePresetTarget.value = preset;
  deletePresetOpen.value = true;
}

async function runDeletePreset() {
  const target = deletePresetTarget.value;
  if (!target) return;
  try {
    await deletePreset.mutateAsync(target.id);
    deletePresetOpen.value = false;
    pendingFilters.delete(String(target.id));
    if (activeTabId.value === String(target.id)) selectTab(ALL_TAB);
  } catch {
    // Keep the confirmation open; the tab still exists.
  }
}

const {
  collectExpertIds,
  experts,
  totalExperts,
  isLoading,
  hasNextPage,
  fetchNextPage,
  isFetchingNextPage,
} = useAdminExperts({ filters });

const selectionMode = ref(false);
const selectedIds = ref(new Set<number>());
const selectingAll = ref(false);
const selectionProgress = ref(0);
const selectionError = ref('');
const bulkActionRunning = ref(false);
const bulkActionMessage = ref('');
const bulkActionDialogOpen = ref(false);
const pendingBulkAction = ref<ExpertBulkAction | null>(null);
const onlyMissingSummaries = ref(true);

function requestBulkAction(action: ExpertBulkAction) {
  if (!selectedIds.value.size || selectingAll.value || bulkActionRunning.value) return;
  if (!action.supportsOnlyMissing) {
    void runBulkAction(action);
    return;
  }
  pendingBulkAction.value = action;
  onlyMissingSummaries.value = true;
  bulkActionDialogOpen.value = true;
}

function closeBulkActionDialog() {
  bulkActionDialogOpen.value = false;
  pendingBulkAction.value = null;
}

function confirmBulkAction() {
  const action = pendingBulkAction.value;
  closeBulkActionDialog();
  if (action) void runBulkAction(action, onlyMissingSummaries.value);
}

let selectionRun = 0;

function clearSelection() {
  closeBulkActionDialog();
  selectionRun++;
  selectingAll.value = false;
  selectedIds.value = new Set();
  selectionError.value = '';
  bulkActionMessage.value = '';
}
function exitSelection() {
  clearSelection();
  selectionMode.value = false;
}
watch([filters, activeTabId], clearSelection, { deep: true, flush: 'sync' });
onBeforeUnmount(() => { selectionRun++; });

function toggleExpertSelection(index: number, rangeSelect: boolean) {
  if (selectingAll.value || bulkActionRunning.value) return;
  const expert = experts.value[index];
  if (!expert) return;
  const next = new Set(selectedIds.value);
  if (rangeSelect) {
    const selectedIndices = experts.value.flatMap((item, position) => next.has(item.id) ? [position] : []);
    resolveShiftRange({ index, selectedIndices, renderedCount: experts.value.length })
      .forEach((position) => next.add(experts.value[position]!.id));
  } else if (next.has(expert.id)) next.delete(expert.id);
  else next.add(expert.id);
  selectedIds.value = next;
  bulkActionMessage.value = '';
}

function cancelSelectAll() {
  selectionRun++;
  selectingAll.value = false;
  selectionProgress.value = 0;
}

async function selectAllInView() {
  if (selectingAll.value) return;
  const run = ++selectionRun;
  selectingAll.value = true;
  selectionProgress.value = 0;
  selectionError.value = '';
  bulkActionMessage.value = '';
  try {
    const ids = await collectExpertIds(() => run !== selectionRun, (count) => { selectionProgress.value = count; });
    if (ids && run === selectionRun) selectedIds.value = ids;
  } catch {
    if (run === selectionRun) selectionError.value = 'Could not select all experts. Your previous selection is unchanged. Please retry.';
  } finally {
    if (run === selectionRun) selectingAll.value = false;
  }
}

async function runBulkAction(action: ExpertBulkAction, onlyMissing = true) {
  if (!selectedIds.value.size || selectingAll.value || bulkActionRunning.value) return;
  const run = selectionRun;
  bulkActionRunning.value = true;
  bulkActionMessage.value = '';
  selectionError.value = '';
  try {
    const result = await action.execute([...selectedIds.value], {
      api,
      onlyMissing,
      onBatchQueued: trackAiBatch,
      onProgress: (message) => { if (run === selectionRun) bulkActionMessage.value = message; },
    });
    if (run === selectionRun) {
      const remaining = new Set(selectedIds.value);
      result.processedIds.forEach((id) => remaining.delete(id));
      selectedIds.value = remaining;
      bulkActionMessage.value = result.message;
      selectionError.value = result.error ?? '';
    }
  } catch {
    if (run === selectionRun) selectionError.value = `${action.label} failed. Please retry.`;
  } finally {
    bulkActionRunning.value = false;
  }
}

const {
  activateApplication,
  publishExpert,
  unpublishExpert,
  setDraft,
  setApplication,
  deleteApplication,
  resendActivation,
} = useExpertStatusActions();

const { loginAs, isLoading: isLoginAsLoading } = useLoginAsExpert();

// Status confirm dialog state
const statusDialogOpen = ref(false);
const statusDialogLoading = ref(false);
const statusDialogAction = ref<'activate' | 'publish' | 'unpublish' | 'setDraft' | 'setApplication' | 'deleteApplication' | null>(null);
const statusDialogExpert = ref<AdminExpertListItem | null>(null);

// Switch type dialog state
const switchTypeDialogOpen = ref(false);
const switchTypeExpert = ref<AdminExpertListItem | null>(null);

function openSwitchTypeDialog(expert: AdminExpertListItem) {
  switchTypeExpert.value = expert;
  switchTypeDialogOpen.value = true;
}

// Reassign level dialog state
const reassignDialogOpen = ref(false);
const reassignExpert = ref<AdminExpertListItem | null>(null);

function openReassignDialog(expert: AdminExpertListItem) {
  reassignExpert.value = expert;
  reassignDialogOpen.value = true;
}

// Invite email dialog state
const inviteDialogOpen = ref(false);
const inviteExpert = ref<AdminExpertListItem | null>(null);

function openInviteDialog(expert: AdminExpertListItem) {
  inviteExpert.value = expert;
  inviteDialogOpen.value = true;
}

// Login as dialog state
const loginAsDialogOpen = ref(false);
const loginAsExpert = ref<AdminExpertListItem | null>(null);
const loginAsAvatarRingClass = computed(() => getExpertTypeAppearance(loginAsExpert.value?.listing_type).avatarRingClass);

function handleStatusAction(
  expert: AdminExpertListItem,
  action: 'activate' | 'publish' | 'unpublish' | 'setDraft' | 'setApplication' | 'deleteApplication',
) {
  statusDialogExpert.value = expert;
  statusDialogAction.value = action;
  statusDialogOpen.value = true;
}

async function confirmStatusAction() {
  if (!statusDialogExpert.value || !statusDialogAction.value) return;

  statusDialogLoading.value = true;
  try {
    const id = statusDialogExpert.value.id;
    switch (statusDialogAction.value) {
      case 'activate':
        await activateApplication.mutateAsync(id);
        break;
      case 'publish':
        await publishExpert.mutateAsync(id);
        break;
      case 'unpublish':
        await unpublishExpert.mutateAsync(id);
        break;
      case 'setDraft':
        await setDraft.mutateAsync(id);
        break;
      case 'setApplication':
        await setApplication.mutateAsync(id);
        break;
      case 'deleteApplication':
        await deleteApplication.mutateAsync(id);
        break;
    }
    statusDialogOpen.value = false;
  } finally {
    statusDialogLoading.value = false;
  }
}

function openLoginAsDialog(expert: AdminExpertListItem) {
  loginAsExpert.value = expert;
  loginAsDialogOpen.value = true;
}

async function confirmLoginAs() {
  if (!loginAsExpert.value) return;
  await loginAs(loginAsExpert.value.user_id);
  loginAsDialogOpen.value = false;
}

async function handleResendActivation(expert: AdminExpertListItem) {
  await resendActivation.mutateAsync(expert.id);
}

function navigateToExpert(expert: AdminExpertListItem) {
  // Flush any pending debounced URL sync so the saved history entry has the
  // up-to-date filters (otherwise typing + clicking within 200ms would lose
  // the latest search term on back navigation).
  if (syncTimer) {
    clearTimeout(syncTimer);
    syncTimer = null;
    syncQueryToUrl();
  }
  // Persist scroll position so we can restore it when the user comes back.
  if (scrollContainer.value) {
    try {
      sessionStorage.setItem(SCROLL_STORAGE_KEY, String(scrollContainer.value.scrollTop));
    } catch {
      // sessionStorage may be unavailable (private mode, etc.) — ignore.
    }
  }
  router.push({ name: 'admin-expert-detail', params: { expertId: expert.id } });
}

function applyStatsFilters(statsFilters: {
  status: ExpertProfileStatus;
  expertType?: ListingType | '';
}) {
  filters.value = {
    ...filters.value,
    status: statsFilters.status,
    expertType: statsFilters.expertType ?? '',
  };
}

// Infinite scroll observer
const sentinel = ref<HTMLElement | null>(null);
let observer: IntersectionObserver | null = null;

watch(sentinel, (el) => {
  if (observer) observer.disconnect();
  if (!el) return;
  observer = new IntersectionObserver(
    (entries) => {
      if (entries[0]?.isIntersecting && hasNextPage.value && !isFetchingNextPage.value) {
        fetchNextPage();
      }
    },
    { rootMargin: '200px' },
  );
  observer.observe(el);
});

// Restore scroll position once the list has rendered after returning from
// the detail page. Only fires once per mount.
let scrollRestored = false;
watch(
  [isLoading, experts, scrollContainer],
  async ([loading, list, container]) => {
    if (scrollRestored || loading || !container || list.length === 0) return;
    let saved: string | null = null;
    try {
      saved = sessionStorage.getItem(SCROLL_STORAGE_KEY);
    } catch {
      // ignore
    }
    if (saved === null) {
      scrollRestored = true;
      return;
    }
    const top = Number(saved);
    if (Number.isFinite(top) && top > 0) {
      await nextTick();
      container.scrollTop = top;
    }
    try {
      sessionStorage.removeItem(SCROLL_STORAGE_KEY);
    } catch {
      // ignore
    }
    scrollRestored = true;
  },
  { immediate: true },
);
</script>

<template>
  <main ref="scrollContainer" class="flex-1 p-8 overflow-y-auto bg-secondary font-light">
    <!-- Header -->
    <div class="flex flex-col md:flex-row md:items-start justify-between mb-6 gap-4">
      <div>
        <UiText as="h1" variant="h3" text="**Expert Manager**" />
        <UiText as="p" variant="p-small" class="text-gray-500 mt-1">
          Manage expert profiles, statuses, and subscriptions
        </UiText>
      </div>
      <div class="flex items-center gap-3">
        <UiText as="span" variant="p-small" class="text-gray-400 tabular-nums">
          {{ totalExperts }} expert{{ totalExperts !== 1 ? 's' : '' }}
        </UiText>
      </div>
    </div>

    <!-- Stats Dashboard -->
    <ExpertStatsPanel
      :active-status="filters.status"
      :active-expert-type="filters.expertType"
      @apply-filters="applyStatsFilters"
    />

    <!-- Saved filter tabs -->
    <ExpertPresetTabs
      :model-value="activeTabId"
      :presets="presets"
      :dirty="isPresetDirty"
      :loading="isPresetsLoading"
      :can-save="hasAnyExpertFilter(filters)"
      class="mb-5"
      @update:model-value="selectTab"
      @create="openCreatePreset"
      @update="savePresetFilters"
      @rename="openRenamePreset"
      @remove="confirmDeletePreset"
      @discard="discardPresetChanges"
    />

    <!-- Search + Filters -->
    <div class="mb-6 space-y-2.5">
      <div class="flex items-center gap-3">
        <UiInputField
          :id="SEARCH_INPUT_ID"
          v-model="searchQuery"
          placeholder="Search by name or email..."
          icon="search"
          size="md"
          class="max-w-sm flex-1"
        />

        <ExpertFilters
          v-model="filters"
          :filter-options="filterOptions"
          :tag-options="tagOptions"
          :tags-loading="isTagsLoading"
          @save-preset="openCreatePreset"
        />

        <UiButton v-if="!selectionMode" label="Select experts" icon="check" variant="secondary" size="md" :disabled="isLoading || !experts.length" @click="selectionMode = true" />

        <UiText
          v-if="activePreset && isPresetDirty"
          as="span"
          variant="caption-normal"
          class="text-amber-600"
        >
          Unsaved changes to “{{ activePreset.name }}”
        </UiText>
      </div>

      <!-- Active filter tags -->
      <Transition
        enter-active-class="transition-all duration-200 ease-out"
        enter-from-class="opacity-0 -translate-y-1"
        enter-to-class="opacity-100 translate-y-0"
        leave-active-class="transition-all duration-150 ease-in"
        leave-from-class="opacity-100 translate-y-0"
        leave-to-class="opacity-0 -translate-y-1"
      >
        <div
          v-if="activeFilters.length > 0"
          class="flex items-center gap-2 flex-wrap"
        >
          <button
            v-for="filter in activeFilters"
            :key="filter.key"
            @click="clearFilter(filter.key)"
            class="group inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium
                   bg-gray-50 text-gray-600 hover:bg-gray-100 hover:text-gray-800
                   border border-gray-100 hover:border-gray-200
                   transition-all duration-150 cursor-pointer"
          >
            <span
              v-if="filter.color"
              class="w-1.5 h-1.5 rounded-full shrink-0"
              :class="filter.color"
            />
            {{ filter.label }}
            <UiIcon name="close" class="w-3 h-3 opacity-40 group-hover:opacity-70 transition-opacity" />
          </button>

          <button
            @click="resetFilters"
            class="text-[11px] text-gray-400 hover:text-gray-600 transition-colors ml-1"
          >
            Clear all
          </button>
        </div>
      </Transition>
    </div>

    <section v-if="selectionMode" aria-label="Expert selection" class="sticky top-0 z-30 mb-4 rounded-lg border border-se-gray-200 bg-white p-4 shadow-sm">
      <div class="flex flex-wrap items-center justify-between gap-3">
        <div class="flex flex-wrap items-center gap-3">
          <UiText as="span" variant="p-small" class="font-semibold tabular-nums" :text="`${selectedIds.size.toLocaleString()} selected`" role="status" />
          <UiButton variant="secondary" size="sm" :disabled="selectingAll || isLoading || !experts.length || bulkActionRunning" :aria-busy="selectingAll" :label="selectingAll ? `Selecting… ${selectionProgress.toLocaleString()}` : 'Select all in view'" @click="selectAllInView">
            <template #before_label>
              <UiIcon v-if="selectingAll" name="refresh-cw" class="h-4 w-4 animate-spin" />
            </template>
          </UiButton>
          <UiButton v-if="selectedIds.size && !selectingAll" label="Clear selection" variant="ghost" size="sm" :disabled="bulkActionRunning" @click="clearSelection" />
        </div>
        <div class="flex flex-wrap items-center gap-2">
          <UiButton v-for="action in expertBulkActions" :key="action.id" :label="bulkActionRunning ? 'Queueing…' : action.label" :aria-busy="bulkActionRunning" :title="action.description" variant="primary" size="sm" :disabled="!selectedIds.size || selectingAll || bulkActionRunning" @click="requestBulkAction(action)" />
          <UiButton :label="selectingAll ? 'Cancel' : 'Done'" variant="ghost" size="sm" :disabled="bulkActionRunning" @click="selectingAll ? cancelSelectAll() : exitSelection()" />
        </div>
      </div>
      <UiText as="p" variant="caption-normal" class="mt-2 text-se-gray-500" text="Click experts to select them. Shift-click to select a range. Select all includes every match in this view." />
      <UiText v-if="selectionError" as="p" variant="p-small" class="mt-2 text-red-600" :text="selectionError" role="alert" />
      <UiText v-if="bulkActionMessage" as="p" variant="p-small" class="mt-2 text-se-gray-600" :text="bulkActionMessage" role="status" />
    </section>

    <section v-if="aiJobs.length || aiTrackingError || aiDiscoveryError" aria-label="AI summary jobs" class="mb-5 space-y-3">
      <UiText as="h2" variant="h5" text="AI summary jobs" />
      <UiText v-if="aiDiscoveryError" as="p" variant="p-small" :text="aiDiscoveryError" role="alert" />
      <UiText v-if="aiTrackingError" as="p" variant="p-small" :text="aiTrackingError" role="alert" />
      <UiCard v-for="job in aiJobs" :key="job.id">
        <div class="flex items-center justify-between gap-3 mb-2">
          <UiText as="span" variant="p-small" class="font-semibold" :text="job.data ? `${job.data.completed} of ${job.data.total} experts finished · ${job.data.status}` : 'Restoring job progress…'" />
          <UiButton v-if="job.finished || job.error" label="Dismiss" variant="ghost" size="sm" @click="dismissAiBatch(job.id)" />
        </div>
        <UiProgressBar :value="job.finishedSteps" :max="job.totalSteps || 1" :indeterminate="!job.data" :tone="job.data?.failed ? 'danger' : 'green'" aria-label="AI summary pipeline steps finished" />
        <UiText v-if="job.data" as="p" variant="caption-normal" class="mt-2 text-se-gray-600" :text="`${job.finishedSteps} of ${job.totalSteps} steps finished${job.data.failed ? ` · ${job.data.failed} experts failed` : ''}`" />
        <UiText v-if="job.data?.status === 'queued'" as="p" variant="p-small" class="mt-2 text-se-gray-600" text="Waiting for processing to start. Progress updates automatically and resumes after refreshing." />
        <UiText v-for="active in job.activeSteps" :key="`${active.expertId}-${active.step}`" as="p" variant="caption-normal" class="mt-1 text-se-gray-600" :text="`Expert #${active.expertId}: ${AI_SUMMARY_STEP_LABELS[active.step]}`" />
        <UiText v-if="job.error" as="p" variant="p-small" class="mt-2 text-red-600" :text="`Could not update progress: ${job.error} Tracking will retry automatically.`" role="alert" />
        <UiText as="p" variant="caption-normal" class="mt-2 text-se-gray-500" :text="`Job ${job.id}`" />
      </UiCard>
    </section>

    <!-- Loading -->
    <LoadingCard v-if="isLoading" />

    <!-- Expert List -->
    <div v-else-if="experts.length > 0" class="space-y-3">
      <ExpertCard
        v-for="(expert, index) in experts"
        :key="expert.id"
        :expert="expert"
        :selection-mode="selectionMode"
        :selected="selectedIds.has(expert.id)"
        :selection-disabled="selectingAll || bulkActionRunning"
        @toggle-selection="(rangeSelect) => toggleExpertSelection(index, rangeSelect)"
        :style="{ animationDelay: `${index * 40}ms` }"
        class="animate-fade-in-up"
        @click="navigateToExpert(expert)"
        @login-as="openLoginAsDialog(expert)"
        @status-action="(action) => handleStatusAction(expert, action)"
        @resend-activation="handleResendActivation(expert)"
        @switch-type="openSwitchTypeDialog(expert)"
        @reassign-level="openReassignDialog(expert)"
        @send-invite="openInviteDialog(expert)"
      />

      <!-- Infinite scroll sentinel -->
      <div ref="sentinel" class="h-1" />

      <div v-if="isFetchingNextPage" class="py-4 text-center">
        <UiText as="p" variant="p-small" class="text-gray-400">Loading more...</UiText>
      </div>
    </div>

    <!-- Empty State -->
    <EmptyDataPlaceholder
      v-else
      title="No experts found"
      description="Try adjusting your search or filters"
    />

    <UiDialog
      v-model="bulkActionDialogOpen"
      title="Generate AI summaries"
      primary-action-label="Generate summaries"
      secondary-action-label="Cancel"
      size="sm"
      @primary-action="confirmBulkAction"
      @secondary-action="closeBulkActionDialog"
      @close="closeBulkActionDialog"
    >
      <div class="space-y-5">
        <UiText as="p" variant="p-small" :text="`${selectedIds.size.toLocaleString()} expert${selectedIds.size === 1 ? '' : 's'} selected. Choose which experts to generate summaries for.`" />
        <UiCheckbox
          v-model="onlyMissingSummaries"
          label="Missing summaries only"
          description="Skip experts who already have both summaries."
        />
        <UiText
          as="p"
          variant="p-small"
          class="text-se-gray-600"
          :text="onlyMissingSummaries
            ? 'Experts missing either summary will run the full pipeline. If they already have one summary, it will also be regenerated.'
            : 'Generate for all selected experts, including those with existing summaries. Existing summaries will be regenerated.'"
        />
      </div>
    </UiDialog>

    <!-- Save / rename tab dialog -->
    <ExpertPresetDialog
      v-model="presetDialogOpen"
      :mode="presetDialogMode"
      :preset="presetDialogTarget"
      :filters="filters"
      :tag-name-by-id="tagNameById"
      :existing-names="presetNames"
      :loading="isPresetMutating"
      @submit="submitPresetDialog"
    />

    <!-- Delete tab confirmation -->
    <UiDialog
      v-model="deletePresetOpen"
      title="Delete tab"
      primaryActionLabel="Delete"
      secondaryActionLabel="Cancel"
      size="sm"
      :loading="isDeletingPreset"
      @primaryAction="runDeletePreset"
      @secondaryAction="deletePresetOpen = false"
      @close="deletePresetOpen = false"
    >
      <UiText as="p" variant="p-small" class="text-gray-600">
        “{{ deletePresetTarget?.name }}” will be removed from your tabs. The experts it matched are
        not affected.
      </UiText>
    </UiDialog>

    <!-- Status Confirm Dialog -->
    <StatusConfirmDialog
      v-model="statusDialogOpen"
      v-model:loading="statusDialogLoading"
      :action="statusDialogAction"
      :expert-name="statusDialogExpert?.display_name ?? ''"
      :current-status="(statusDialogExpert?.profile_status as ExpertProfileStatus) ?? 'draft'"
      @confirm="confirmStatusAction"
    />

    <!-- Switch Type Dialog -->
    <ExpertTypeSwitchDialog
      v-if="switchTypeExpert"
      v-model="switchTypeDialogOpen"
      :expert-id="switchTypeExpert.id"
      :expert-name="switchTypeExpert.display_name"
      :current-listing-type="switchTypeExpert.listing_type"
    />

    <!-- Reassign Level Dialog -->
    <ReassignExpertLevelDialog
      v-if="reassignExpert"
      v-model="reassignDialogOpen"
      :expert-id="reassignExpert.id"
      :expert-name="reassignExpert.display_name"
    />

    <!-- Invite Email Dialog -->
    <ExpertInviteEmailDialog
      v-if="inviteExpert"
      v-model="inviteDialogOpen"
      :expert-id="inviteExpert.id"
    />

    <!-- Login As Dialog -->
    <UiDialog
      v-model="loginAsDialogOpen"
      title="Login as Expert"
      primaryActionLabel="Login As Expert"
      secondaryActionLabel="Cancel"
      size="sm"
      :loading="isLoginAsLoading"
      @primaryAction="confirmLoginAs"
      @secondaryAction="loginAsDialogOpen = false"
      @close="loginAsDialogOpen = false"
    >
      <div v-if="loginAsExpert" class="flex items-center gap-4 mb-4">
        <UiAvatar
          :name="loginAsExpert.display_name"
          :url="loginAsExpert.avatar ?? undefined"
          size="xs"
          :responsive="false"
          color-coded
          class="ring-2 ring-offset-2 ring-offset-white"
          :class="loginAsAvatarRingClass"
        />
        <div>
          <UiText as="p" variant="p" class="font-medium text-gray-900">
            {{ loginAsExpert.display_name }}
          </UiText>
          <UiText as="p" variant="p-small" class="text-gray-500">
            {{ loginAsExpert.email }}
          </UiText>
        </div>
      </div>
      <UiText as="p" variant="p-small" class="text-gray-600">
        You will be redirected to their dashboard where you can view and manage their account.
      </UiText>
    </UiDialog>
  </main>
</template>

<style scoped>
@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.animate-fade-in-up {
  animation: fadeInUp 0.3s ease-out both;
}
</style>
